import { useState, useCallback, useRef } from 'react';

export type ResourceStatus = 'not_started' | 'viewed' | 'completed';

const PROGRESS_KEY = 'careweave_resource_progress';
const XP_AWARDED_KEY = 'careweave_resource_xp_awarded';

function loadRecord<T>(key: string): Record<string, T> {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw) as Record<string, T>;
  } catch {
  }
  return {};
}

function saveRecord<T>(key: string, data: Record<string, T>) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
  }
}

export function useResourceProgress() {
  const [progress, setProgress] = useState<Record<string, ResourceStatus>>(() =>
    loadRecord<ResourceStatus>(PROGRESS_KEY)
  );
  const [xpAwarded, setXpAwarded] = useState<Record<string, boolean>>(() =>
    loadRecord<boolean>(XP_AWARDED_KEY)
  );

  // Use a ref to avoid stale closure reads for XP award checks
  const xpAwardedRef = useRef(xpAwarded);
  xpAwardedRef.current = xpAwarded;

  const progressRef = useRef(progress);
  progressRef.current = progress;

  const getStatus = useCallback((id: string): ResourceStatus => {
    return progressRef.current[id] ?? 'not_started';
  }, []);

  const markViewed = useCallback((id: string) => {
    setProgress(prev => {
      if (prev[id] === 'viewed' || prev[id] === 'completed') return prev;
      const next = { ...prev, [id]: 'viewed' as ResourceStatus };
      saveRecord(PROGRESS_KEY, next);
      return next;
    });
  }, []);

  const cycleStatus = useCallback((id: string): { newStatus: ResourceStatus; isFirstCompletion: boolean } => {
    const current = progressRef.current[id] ?? 'not_started';
    let newStatus: ResourceStatus;
    if (current === 'not_started') newStatus = 'viewed';
    else if (current === 'viewed') newStatus = 'completed';
    else newStatus = 'not_started';

    setProgress(prev => {
      const next = { ...prev, [id]: newStatus };
      saveRecord(PROGRESS_KEY, next);
      return next;
    });

    const alreadyAwarded = xpAwardedRef.current[id] ?? false;
    const isFirstCompletion = newStatus === 'completed' && !alreadyAwarded;

    if (isFirstCompletion) {
      // Mark as awarded immediately in the ref to prevent double-award on rapid clicks
      xpAwardedRef.current = { ...xpAwardedRef.current, [id]: true };
      setXpAwarded(prev => {
        const next = { ...prev, [id]: true };
        saveRecord(XP_AWARDED_KEY, next);
        return next;
      });
    }

    return { newStatus, isFirstCompletion };
  }, []);

  const markCompleted = useCallback((id: string): { isFirstCompletion: boolean } => {
    setProgress(prev => {
      const next = { ...prev, [id]: 'completed' as ResourceStatus };
      saveRecord(PROGRESS_KEY, next);
      return next;
    });

    const alreadyAwarded = xpAwardedRef.current[id] ?? false;
    const isFirstCompletion = !alreadyAwarded;

    if (isFirstCompletion) {
      xpAwardedRef.current = { ...xpAwardedRef.current, [id]: true };
      setXpAwarded(prev => {
        const next = { ...prev, [id]: true };
        saveRecord(XP_AWARDED_KEY, next);
        return next;
      });
    }

    return { isFirstCompletion };
  }, []);

  const completedCount = Object.values(progress).filter(s => s === 'completed').length;
  const viewedCount = Object.values(progress).filter(s => s === 'viewed').length;

  return { getStatus, markViewed, cycleStatus, markCompleted, completedCount, viewedCount, progress };
}
